import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAuth, LinkedAccount } from '../contexts/AuthContext';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Field } from './ui/Field';
import { API_URL } from '../services/api';
import { unwrapResponse } from '../utils/unwrapResponse';
import { IconButton } from './ui/IconButton';
import { queryKeys } from '../hooks/queries/queryKeys';

// ─── Badge state machine ─────────────────────────────────────────────────────

type ProbeStatus = 'probing' | 'active' | 'needs_reauth' | 'network_error';

/** Map of userId → ProbeStatus. 'self' key is used for the active user. */
type ProbeStatuses = Record<string, ProbeStatus>;

// ─── AddAccountModal ─────────────────────────────────────────────────────────

interface AddAccountModalProps {
  onClose: () => void;
  onSubmit: (email: string, password: string) => Promise<void>;
  initialEmail?: string;
}

const AddAccountModal: React.FC<AddAccountModalProps> = ({ onClose, onSubmit, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try { await onSubmit(email, password); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to link account'); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Add Account" size="sm">
      <p className="text-[13px] text-text-secondary m-0 mb-4">
        Link another account for instant switching. Both accounts must have fast switch enabled by an admin.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Field label="Email or Username"><Input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email or username" required /></Field>
        <Field label="Password"><Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required /></Field>
        {error && <div className="text-xs text-danger py-1 px-4">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={loading || !email || !password}>
            {loading ? 'Linking...' : 'Link Account'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── AccountItem ─────────────────────────────────────────────────────────────

interface AccountItemProps {
  name?: string;
  email?: string;
  probeStatus?: ProbeStatus;
  onSwitch?: () => void;
  onRemove?: () => void;
  onRelink?: () => void;
  switching?: boolean;
}

const AccountItem: React.FC<AccountItemProps> = ({
  name, email: userEmail, probeStatus, onSwitch, onRemove, onRelink, switching,
}) => {
  const isActive = probeStatus === 'active';
  const isProbing = probeStatus === 'probing';
  const needsReauth = probeStatus === 'needs_reauth';
  const networkError = probeStatus === 'network_error';

  const handleClick = () => {
    if (needsReauth && onRelink) { onRelink(); return; }
    if (onSwitch) onSwitch();
  };

  return (
    <div className="flex items-center gap-1 pr-2">
      <button
        className="flex-1 flex items-center gap-2 py-1.5 px-4 border-none bg-transparent cursor-pointer text-left w-full text-text-primary rounded transition-colors hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-wait"
        onClick={handleClick}
        disabled={switching || (!onSwitch && !onRelink)}
      >
        <div className="w-7 h-7 rounded-full bg-aegean text-text-on-accent flex items-center justify-center text-[13px] font-semibold shrink-0">
          {name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-[13px] font-medium whitespace-nowrap overflow-hidden text-ellipsis">{name}</span>
          <span className="text-[11px] text-text-secondary whitespace-nowrap overflow-hidden text-ellipsis">{userEmail}</span>
        </div>
        <div className="ml-auto shrink-0 flex items-center">
          {isProbing && (
            <span className="text-xs text-text-secondary animate-pulse">...</span>
          )}
          {isActive && !isProbing && (
            <span className="text-[10px] font-semibold uppercase text-success">Active</span>
          )}
          {needsReauth && !isProbing && (
            <span className="text-[10px] font-semibold uppercase text-warning">Re-link</span>
          )}
          {networkError && !isProbing && (
            <span
              className="text-[10px] font-semibold text-text-secondary cursor-help"
              title="Couldn't verify session — try again later"
            >
              ?
            </span>
          )}
          {switching && (
            <span className="text-xs text-text-secondary animate-pulse">...</span>
          )}
        </div>
      </button>
      {onRemove && (
        <IconButton
          icon={<span aria-hidden="true">&times;</span>}
          aria-label="Remove linked account"
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove linked account"
          className="shrink-0"
        />
      )}
    </div>
  );
};

// ─── useAccountProbeStatuses (TanStack Query, shared cache) ──────────────────
//
// Both NavQuickSwitch and AccountSwitcher mount in PillarLayout. Pre-2026-05-10
// each instance owned its own state, which doubled network calls AND raced
// non-idempotent state updates (unlinkAccount on refresh failure ran twice).
// A 2026-05-10 hotfix hoisted state to a hand-rolled module singleton with a
// subscriber set; Spec 296 Package A replaces that with a single shared
// `useQuery` cache entry (`queryKeys.account.probeStatuses()`) — every
// mounted consumer observes the SAME query, so TanStack Query's own request
// dedupe (one in-flight fetch per key, shared across observers) gives the
// "only one probe pass for N mounted consumers" guarantee the module
// singleton existed for, without hand-rolled subscriber plumbing.
//
// Refresh-failure policy: do NOT auto-unlink the account. The badge renders
// 'Re-link' and the user clicks to re-authenticate via the Add Account modal.
// Pre-fix the unlink ran synchronously, removing the AccountItem from the DOM
// before the badge could render — silent unlink with no relink affordance.

interface ProbeRefreshDeps {
  updateLinkedAccount: (a: LinkedAccount) => void;
  logout: () => void;
}

async function probeAccounts(
  authToken: string,
  linkedAccounts: LinkedAccount[],
  deps: ProbeRefreshDeps,
): Promise<ProbeStatuses> {
  const snapshot = linkedAccounts.slice();

  const probeSelf = async (): Promise<ProbeStatus> => {
    try {
      await axios.get(`${API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return 'active';
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        deps.logout();
        return 'needs_reauth';
      }
      return 'network_error';
    }
  };

  const probeLinked = async (account: LinkedAccount): Promise<[string, ProbeStatus]> => {
    try {
      await axios.get(`${API_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${account.accessToken}` },
      });
      return [account.userId, 'active'];
    } catch (err: unknown) {
      if (!axios.isAxiosError(err) || !err.response) {
        return [account.userId, 'network_error'];
      }
      if (err.response.status !== 401) {
        return [account.userId, 'network_error'];
      }
      try {
        const refreshResp = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken: account.refreshToken,
        });
        const body = unwrapResponse<{ token: string; refreshToken: string }>(refreshResp.data);
        const updated: LinkedAccount = {
          ...account,
          accessToken: body.token,
          refreshToken: body.refreshToken,
        };
        deps.updateLinkedAccount(updated);
        return [account.userId, 'active'];
      } catch {
        // Refresh dead — surface 'Re-link' badge; do NOT auto-unlink (badge
        // would never render because the AccountItem would unmount first).
        return [account.userId, 'needs_reauth'];
      }
    }
  };

  const [selfResult, ...linkedResults] = await Promise.allSettled([
    probeSelf(),
    ...snapshot.map(a => probeLinked(a)),
  ]);

  const next: ProbeStatuses = {};
  next['self'] = selfResult.status === 'fulfilled' ? selfResult.value : 'network_error';
  for (const result of linkedResults) {
    if (result.status === 'fulfilled') {
      const [userId, status] = result.value;
      next[userId] = status;
    }
  }
  return next;
}

/**
 * Probes the active session and all linked accounts to determine token validity.
 * Returns a ProbeStatuses map with 'self' for the current user and userId keys
 * for linked accounts. Backed by one shared `useQuery` cache entry — see
 * module comment above for rationale.
 *
 * @param active  Whether the consumer is currently visible/open. Re-probing
 *                (beyond the query's own mount fetch) only runs when this
 *                becomes true, mirroring the pre-migration dropdown-open
 *                trigger. `staleTime` (5s) reproduces the old cooldown for
 *                the mount-driven fetch; the manual `dataUpdatedAt` check
 *                below reproduces it for the explicit re-probe triggers,
 *                since `refetch()` itself ignores `staleTime`.
 */
function useAccountProbeStatuses(active: boolean): ProbeStatuses {
  const { linkedAccounts, logout, updateLinkedAccount } = useAuth();
  const authToken = localStorage.getItem('authToken') ?? '';
  const linkedUserIds = linkedAccounts.map(a => a.userId).join(',');

  const query = useQuery<ProbeStatuses>({
    queryKey: queryKeys.account.probeStatuses(),
    queryFn: () => probeAccounts(authToken, linkedAccounts, { updateLinkedAccount, logout }),
    staleTime: 5_000,
    // Pre-migration code was a single Promise.allSettled pass with no retry
    // wrapper (confirmed above — probeSelf/probeLinked never throw, they
    // resolve to a status) — opting out explicitly for consistency with
    // every other migrated query in this codebase.
    retry: false,
  });

  const maybeReprobe = () => {
    if (Date.now() - query.dataUpdatedAt < 5_000) return;
    query.refetch();
  };

  // Re-probe when the set of linked accounts changes — mirrors the
  // pre-migration `runProbes` re-running whenever its `linkedUserIds`
  // dependency changed (e.g. after linking/unlinking an account).
  useEffect(() => {
    maybeReprobe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedUserIds]);

  // Re-probe when the consumer becomes active (dropdown opens).
  useEffect(() => {
    if (active) maybeReprobe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // While a (re)probe is in flight, every account shows 'probing' — matches
  // the pre-migration behavior of setting the whole map to 'probing'
  // synchronously before each pass (not just the very first one).
  if (query.isFetching) {
    const probing: ProbeStatuses = { self: 'probing' };
    for (const a of linkedAccounts) probing[a.userId] = 'probing';
    return probing;
  }

  return query.data ?? {};
}

// ─── NavQuickSwitch ──────────────────────────────────────────────────────────

export const NavQuickSwitch: React.FC = () => {
  const { user, linkedAccounts, switchToAccount, unlinkAccount, linkAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [relinkEmail, setRelinkEmail] = useState('');
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const statuses = useAccountProbeStatuses(open);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!user?.allowFastSwitch && linkedAccounts.length === 0) return null;

  const handleSwitch = async (account: LinkedAccount) => {
    setError(''); setSwitching(account.userId);
    try { await switchToAccount(account.userId); setOpen(false); }
    catch (err) { setError(err instanceof Error ? err.message : 'Switch failed'); }
    finally { setSwitching(null); }
  };

  const handleRelink = (email: string) => {
    setRelinkEmail(email);
    setShowAddModal(true);
    setOpen(false);
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          className="px-3.5 py-2 bg-transparent border border-border-light rounded-lg cursor-pointer text-text-secondary font-medium text-sm transition-all hover:bg-[var(--theme-bg-tertiary)] hover:border-[var(--theme-border-medium)] hover:text-text-primary"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-haspopup="true"
        >
          Switch Account
        </button>
        {open && (
          <div className="absolute top-full right-0 mt-1 min-w-[260px] bg-bg-elevated border border-border-light rounded-lg shadow-lg z-[200] py-2">
            <AccountItem
              name={user?.name}
              email={user?.email}
              probeStatus={statuses['self']}
            />
            {linkedAccounts.map(account => (
              <AccountItem
                key={account.userId}
                name={account.name}
                email={account.email}
                probeStatus={statuses[account.userId]}
                onSwitch={() => handleSwitch(account)}
                onRemove={() => unlinkAccount(account.userId)}
                onRelink={() => handleRelink(account.email)}
                switching={switching === account.userId}
              />
            ))}
            {error && <div className="text-xs text-danger py-1 px-4">{error}</div>}
            <button
              className="block w-[calc(100%-16px)] mx-2 mt-1 py-1.5 px-2 border border-dashed border-border-default bg-transparent text-text-secondary cursor-pointer rounded text-xs text-center transition-all hover:border-accent hover:text-accent hover:bg-bg-tertiary"
              onClick={() => { setShowAddModal(true); setRelinkEmail(''); setOpen(false); }}
            >
              + Add Account
            </button>
          </div>
        )}
      </div>
      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onSubmit={linkAccount}
          initialEmail={relinkEmail}
        />
      )}
    </>
  );
};

// ─── AccountSwitcher ─────────────────────────────────────────────────────────

const AccountSwitcher: React.FC = () => {
  const { user, linkedAccounts, switchToAccount, unlinkAccount, linkAccount } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [relinkEmail, setRelinkEmail] = useState('');
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState('');

  // AccountSwitcher is always mounted (not dropdown-gated), so probe on mount.
  const statuses = useAccountProbeStatuses(true);

  if (!user?.allowFastSwitch && linkedAccounts.length === 0) return null;

  const handleSwitch = async (account: LinkedAccount) => {
    setError(''); setSwitching(account.userId);
    try { await switchToAccount(account.userId); }
    catch (err) { setError(err instanceof Error ? err.message : 'Switch failed'); }
    finally { setSwitching(null); }
  };

  const handleRelink = (email: string) => {
    setRelinkEmail(email);
    setShowAddModal(true);
  };

  return (
    <>
      <div className="border-t border-border-default pt-2 mt-1">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary py-1 px-4 pb-2">Switch Account</div>
        <AccountItem
          name={user?.name}
          email={user?.email}
          probeStatus={statuses['self']}
        />
        {linkedAccounts.map(account => (
          <AccountItem
            key={account.userId}
            name={account.name}
            email={account.email}
            probeStatus={statuses[account.userId]}
            onSwitch={() => handleSwitch(account)}
            onRemove={() => unlinkAccount(account.userId)}
            onRelink={() => handleRelink(account.email)}
            switching={switching === account.userId}
          />
        ))}
        {error && <div className="text-xs text-danger py-1 px-4">{error}</div>}
        <button
          className="block w-[calc(100%-16px)] mx-2 mt-1 py-1.5 px-2 border border-dashed border-border-default bg-transparent text-text-secondary cursor-pointer rounded text-xs text-center transition-all hover:border-accent hover:text-accent hover:bg-bg-tertiary"
          onClick={() => { setShowAddModal(true); setRelinkEmail(''); }}
        >
          + Add Account
        </button>
      </div>
      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onSubmit={linkAccount}
          initialEmail={relinkEmail}
        />
      )}
    </>
  );
};

export default AccountSwitcher;
